import React, { useEffect, useState } from 'react';
import { firebaseInitError, isFirebaseConfigured, signInWithGoogle } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import './Login.css';

const Login = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    const from = location.state?.from?.pathname || '/';
    navigate(from, { replace: true });
  }, [currentUser, location.state, navigate]);

  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setLoading(true);
      await signInWithGoogle();
    } catch (error) {
      // If Firebase isn't configured, show the actionable setup error.
      if (!isFirebaseConfigured) {
        setError(firebaseInitError || 'Firebase authentication is not configured.');
        // eslint-disable-next-line no-console
        console.error(error);
        return;
      }

      const code = error?.code;
      if (code === 'auth/unauthorized-domain') {
        setError(
          'This website domain is not authorized for Google sign-in. Add it under Firebase Console → Authentication → Settings → Authorized domains.'
        );
      } else if (code === 'auth/popup-blocked') {
        setError('Your browser blocked the sign-in popup. Please allow popups and try again.');
      } else if (code === 'auth/popup-closed-by-user') {
        setError('Sign-in popup was closed before completing. Please try again.');
      } else {
        setError('Failed to sign in with Google. Check console for details.');
      }

      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (currentUser) return null;

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>Welcome to DSA Verse</h1>
          <p>Sign in to access all features and save your progress</p>
        </div>
        
        {!isFirebaseConfigured && (
          <div className="warning-message">
            <strong>⚠️ Setup Required</strong>
            <p>Firebase authentication is not configured yet.</p>
            <p>Please check the console or see <strong>FIRESTORE_SETUP_INSTRUCTIONS.md</strong> for instructions.</p>
          </div>
        )}
        
        {error && <div className="error-message">{error}</div>}
        
        <button 
          onClick={handleGoogleSignIn}
          disabled={loading || !isFirebaseConfigured}
          className="google-signin-btn"
        >
          <svg className="google-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          {loading ? 'Signing In...' : 'Sign in with Google'}
        </button>
      </div>
    </div>
  );
};

export default Login;
